// ===========================================
// QuickActions Component
// ===========================================
// Grid of shortcut buttons for common actions

import React from 'react';
import { Card, CardContent, CardHeader, Grid, Button, Typography, Divider, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// ===========================================
// TYPES
// ===========================================

// Brand color mappings
const brandColors: Record<string, { bg: string; text: string; hover: string }> = {
  blue: { bg: '#4580E4', text: '#ffffff', hover: '#3a6fc7' },
  yellow: { bg: '#FFCE00', text: '#ffffff', hover: '#e6b900' },
  mint: { bg: '#96DAC9', text: '#ffffff', hover: '#7dcbb5' },
  coral: { bg: '#FFAE9E', text: '#ffffff', hover: '#ff9a87' },
};

export interface QuickAction {
  label: string;
  subtitle?: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: () => void;
  color?: 'primary' | 'secondary' | 'blue' | 'yellow' | 'mint' | 'coral';
  disabled?: boolean;
}

export interface QuickActionsProps {
  actions: QuickAction[];
  columns?: 2 | 3 | 4;
  title?: string;
}

// ===========================================
// COMPONENT
// ===========================================

export function QuickActions({
  actions,
  columns = 2,
  title = 'Quick Actions',
}: QuickActionsProps) {
  const navigate = useNavigate();

  const handleClick = (action: QuickAction) => {
    if (action.href) {
      navigate(action.href);
    } else if (action.onClick) {
      action.onClick();
    }
  };

  // Calculate grid columns based on prop
  const gridSize = 12 / columns;

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        }
        sx={{ pb: 0 }}
      />
      <Divider sx={{ mx: 2, mt: 2 }} />
      <CardContent sx={{ pt: 1, pb: '8px !important' }}>
        <Grid container spacing={1.5}>
          {actions.map((action, index) => (
            <Grid item xs={6} sm={gridSize} key={index}>
              {(() => {
                const brandColor = brandColors[action.color || ''];
                const isBrandColor = !!brandColor;

                return (
                  <Button
                    variant={action.color === 'secondary' ? 'outlined' : 'contained'}
                    color={isBrandColor ? undefined : (action.color as 'primary' | 'secondary') || 'primary'}
                    onClick={() => handleClick(action)}
                    disabled={action.disabled}
                    fullWidth
                    sx={{
                      py: 1,
                      flexDirection: 'column',
                      gap: 0.25,
                      height: action.subtitle ? 90 : 80,
                      textAlign: 'center',
                      ...(isBrandColor && {
                        bgcolor: brandColor.bg,
                        color: brandColor.text,
                        '&:hover': {
                          bgcolor: brandColor.hover,
                        },
                      }),
                    }}
                  >
                    <Box sx={{ fontSize: 24, lineHeight: 1 }}>{action.icon}</Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.2,
                        textTransform: 'none',
                      }}
                    >
                      {action.label}
                    </Typography>
                    {action.subtitle && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          lineHeight: 1,
                          opacity: 0.85,
                          textTransform: 'none',
                        }}
                      >
                        {action.subtitle}
                      </Typography>
                    )}
                  </Button>
                );
              })()}
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default QuickActions;
